import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { X, ShieldCheck, Loader2, CheckCircle2, Circle } from "lucide-react";
import OrangeBySiteLogo from "@/components/site/OrangeBySiteLogo";

const API = process.env.REACT_APP_BACKEND_URL;
const money = (m, c) => `${({ GBP: "£", USD: "$", EUR: "€", AED: "AED " })[c] || c + " "}${(Number(m || 0) / 100).toFixed(2)}`;

const PLACEHOLDER_RX = /LEGAL[_ ]REVIEW[_ ]REQUIRED|NOT LEGALLY APPROVED|clause body to be provided by solicitor|\bDRAFT\b/i;

/**
 * Phase 3H + Phase 3I.4N-B-2 — customer-facing agreement review + acceptance.
 *
 * Standard (single-agreement) journey remains unchanged.
 *
 * For a bundle (combined OrangeCare + Orange Optimise Pro) the response
 * carries `bundle_siblings` (each with its own signed token). The UI
 * renders BOTH agreements as tabs; the customer must scroll + check +
 * accept each one independently. CONTINUE TO PAYMENT is disabled until
 * EVERY agreement in the bundle is ACCEPTED. The backend `/proceed`
 * endpoint re-verifies bundle authorisation via
 * `is_bundle_payment_authorised()` — the frontend is not the security
 * boundary.
 */
export default function AgreementReviewPage() {
  const { token } = useParams();
  // Array of { token, agreement, checked, scrolled, busy } per bundle item.
  const [items, setItems] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showTermsIdx, setShowTermsIdx] = useState(null);
  const [proceeding, setProceeding] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/agreements/${token}`)
      .then((r) => {
        const primary = { token, agreement: r.data,
                          checked: false,
                          scrolled: !!r.data.scrolled_to_end_at || r.data.status === "ACCEPTED" };
        const siblings = (r.data.bundle_siblings || []).map((s) => ({
          token: s.token, agreement: s,
          checked: false,
          scrolled: !!s.scrolled_to_end_at || s.status === "ACCEPTED",
        }));
        setItems([primary, ...siblings]);
      })
      .catch(() => setErr("This agreement link is invalid or has expired."));
  }, [token]);

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const openTerms = async (idx) => {
    setShowTermsIdx(idx);
    try { await axios.post(`${API}/api/agreements/${items[idx].token}/opened`); } catch {}
  };

  const proceed = async () => {
    if (proceeding || !items) return;
    setProceeding(true);
    try {
      // 1. Record acceptance for each agreement not yet accepted.
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.agreement.status !== "ACCEPTED") {
          await axios.post(`${API}/api/agreements/${it.token}/accept`,
                            { checkbox_confirmed: true });
        }
      }
      // 2. Kick off the payment flow via the PRIMARY token — the backend
      //    /proceed handler is bundle-aware and refuses to expose a
      //    provider approve_url unless every sister agreement is ACCEPTED.
      const primaryToken = items[0].token;
      const { data } = await axios.post(`${API}/api/agreements/${primaryToken}/proceed`);
      if (data?.approve_url) { window.location.assign(data.approve_url); return; }
      setErr("We couldn't reach the payment provider. Please try again shortly.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const reason = (typeof d === "object" && d)
        ? (d.reason || d.code || "Could not record acceptance")
        : (typeof d === "string" ? d : "Could not record acceptance");
      setErr(reason);
    } finally { setProceeding(false); }
  };

  // Bundle mode requires ALL items scrolled + checked; single agreement
  // preserves the original behaviour.
  const canContinue = useMemo(() => {
    if (!items || proceeding) return false;
    return items.every((it) => {
      const legalUnready = it.agreement.legal_review_required === true
        || PLACEHOLDER_RX.test(it.agreement.legal_text || "");
      if (legalUnready) return false;
      return it.scrolled && it.checked;
    });
  }, [items, proceeding]);

  if (err) return (
    <div className="min-h-screen bg-[#F6F5F2] px-4 py-10 md:py-16" data-testid="agreement-error">
      <div className="max-w-md mx-auto">
        <div className="mb-8"><OrangeBySiteLogo size="md" tone="black" /></div>
        <div className="bg-white rounded-2xl border border-[#EAEAEA] p-6 md:p-8">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Agreement</div>
          <h1 className="mt-1 font-black text-xl md:text-2xl">Link no longer valid</h1>
          <p className="mt-3 text-sm text-[#4A4A4A] leading-relaxed">{err}</p>
          <a href="/" className="mt-6 inline-flex w-full items-center justify-center py-3 rounded-full font-black text-[12px] tracking-[0.16em] uppercase bg-[#FF6A00] text-black hover:bg-[#e75f00]">
            Back to OrangeBySite →
          </a>
        </div>
      </div>
    </div>
  );
  if (!items) return (
    <div className="min-h-screen bg-[#F6F5F2] flex flex-col items-center justify-center gap-6" data-testid="agreement-loading">
      <OrangeBySiteLogo size="md" tone="black" />
      <Loader2 className="animate-spin text-[#FF6A00]" />
    </div>
  );

  const isBundle = items.length > 1;
  const active = items[activeIdx];
  const a = active.agreement;
  const legalUnready = a.legal_review_required === true || PLACEHOLDER_RX.test(a.legal_text || "");
  const c = a.commercial_snapshot || {};

  return (
    <div className="min-h-screen bg-[#F6F5F2]" data-testid="agreement-page">
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <div className="mb-8 md:mb-10">
          <OrangeBySiteLogo size="md" tone="black" testId="agreement-page-logo" />
        </div>

        {isBundle && (
          <div className="mb-4 rounded-xl border border-[#FCE1C7] bg-[#FFF7EE] p-4" data-testid="agreement-bundle-banner">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">
              Combined subscription
            </div>
            <p className="mt-1 text-sm text-[#4A4A4A]">
              This order bundles {items.length} agreements. Please review and accept each one before continuing to payment.
            </p>
          </div>
        )}

        {isBundle && (
          <div className="mb-4 flex flex-wrap gap-2" data-testid="agreement-bundle-tabs">
            {items.map((it, i) => {
              const acc = it.agreement.status === "ACCEPTED" || it.checked;
              const label = (it.agreement.service_type || "").replace(/_/g, " ");
              return (
                <button
                  key={it.agreement.id}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  data-testid={`agreement-bundle-tab-${i}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-black tracking-[0.14em] uppercase border ${
                    i === activeIdx ? "bg-[#0A0A0A] text-white border-black" : "bg-white text-[#0A0A0A] border-[#EAEAEA]"
                  }`}
                >
                  {acc
                    ? <CheckCircle2 size={12} className="text-[#0FA958]"/>
                    : <Circle size={12} className="text-[#8A8A8A]"/>}
                  {label}
                </button>
              );
            })}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#EAEAEA] p-6 md:p-8">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Review order</div>
          <h1 className="mt-1 font-black text-2xl md:text-3xl" data-testid="agreement-service">
            {(c.service_type || "").replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
          </h1>

          <dl className="mt-6 space-y-2 text-sm">
            <Row label="Plan" testId="agreement-plan">
              {(c.service_type || "").replace(/_/g, " ")} · {c.billing_interval || "one-off"}
            </Row>
            <Row label="Billing frequency" testId="agreement-cycle">{c.billing_interval || "one-off"}</Row>
            <Row label="Currency" testId="agreement-currency">{c.currency}</Row>
            <Row label="Territory" testId="agreement-territory">{a.territory}</Row>
            <Row label="Agreement reference" testId="agreement-ref">{a.agreement_reference}</Row>
            <Row label="Version" testId="agreement-version">v{a.template_version}</Row>
          </dl>

          <div className="mt-6 p-4 rounded-xl bg-[#0A0A0A] text-white">
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">
              {a.payment_schedule ? "Project total" : "Amount due today"}
            </div>
            <div className="mt-1 font-black text-3xl" data-testid="agreement-total">
              {money(a.project_snapshot?.project_value_minor ?? c.amount_minor, c.currency)}
            </div>
            {c.billing_interval && (
              <div className="mt-2 text-[12px] opacity-80" data-testid="agreement-recurring">
                Renews {c.billing_interval === "annual" ? "annually" : "monthly"} at
                {" "}{money(c.amount_minor, c.currency)} until cancelled per the applicable Terms & Conditions.
              </div>
            )}
          </div>

          {a.legal_review_required && (
            <div className="mt-4 p-3 rounded-lg bg-[#FFECEC] border border-[#F3B9A2] text-[12px] text-[#8B2A0F]"
                 data-testid="agreement-draft-banner">
              <strong>This agreement is not yet available for acceptance.</strong> Legal review is still required. Please contact OrangeBySite.
            </div>
          )}

          <div className="mt-6 border-t pt-6">
            <button
              type="button"
              data-testid={`agreement-view-terms-${activeIdx}`}
              onClick={() => openTerms(activeIdx)}
              className="text-[11px] font-black tracking-[0.16em] uppercase text-[#FF6A00] hover:underline"
            >
              View Terms &amp; Conditions →
            </button>
            <a
              href={`${API}/api/agreements/${active.token}/pdf`}
              target="_blank" rel="noreferrer"
              data-testid={`agreement-download-pdf-${activeIdx}`}
              className="ml-4 text-[11px] font-black tracking-[0.16em] uppercase text-[#8A8A8A] hover:underline"
            >
              Download PDF copy →
            </a>

            <label className={`mt-4 flex items-start gap-3 text-sm ${(!legalUnready && active.scrolled) ? "" : "opacity-50"}`}
                   data-testid={`agreement-checkbox-row-${activeIdx}`}>
              <input type="checkbox" data-testid={`agreement-checkbox-${activeIdx}`}
                     disabled={legalUnready || !active.scrolled} checked={active.checked}
                     onChange={(e) => updateItem(activeIdx, { checked: e.target.checked })}
                     className="mt-1"/>
              <span>I have read and I agree to the OrangeBySite Terms &amp; Conditions ({a.agreement_reference}).</span>
            </label>

            {isBundle && (
              <p className="mt-3 text-[11px] text-[#8A8A8A]" data-testid={`agreement-bundle-progress-${activeIdx}`}>
                {items.filter((it) => it.checked || it.agreement.status === "ACCEPTED").length} of {items.length} agreements accepted.
              </p>
            )}

            <button
              type="button"
              data-testid="agreement-continue"
              onClick={proceed}
              disabled={!canContinue}
              className={`mt-4 w-full py-3 rounded-full font-black text-[12px] tracking-[0.16em] uppercase ${
                canContinue ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"
              }`}
            >
              {proceeding ? "…" : "Continue to Payment →"}
            </button>
          </div>
        </div>
      </div>

      {showTermsIdx !== null && items[showTermsIdx] && (
        <TermsModal
          token={items[showTermsIdx].token}
          legalText={items[showTermsIdx].agreement.legal_text}
          reference={items[showTermsIdx].agreement.agreement_reference}
          onScrolledToEnd={() => updateItem(showTermsIdx, { scrolled: true })}
          onClose={() => setShowTermsIdx(null)}
        />
      )}
    </div>
  );
}

function Row({ label, testId, children }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[#8A8A8A]">{label}</dt>
      <dd className="font-bold text-right" data-testid={testId}>{children}</dd>
    </div>
  );
}

function TermsModal({ token, legalText, reference, onScrolledToEnd, onClose }) {
  const scrollRef = useRef(null);
  const [atBottom, setAtBottom] = useState(false);
  const firedRef = useRef(false);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 16 && !firedRef.current) {
      firedRef.current = true;
      setAtBottom(true);
      onScrolledToEnd?.();
      axios.post(`${API}/api/agreements/${token}/scrolled`).catch(() => {});
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-stretch md:items-center justify-center p-0 md:p-6" data-testid="agreement-terms-modal">
      <div className="bg-white w-full md:max-w-2xl md:rounded-2xl flex flex-col max-h-full">
        <div className="flex items-center justify-between p-5 border-b gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <OrangeBySiteLogo
              size="sm" tone="black" linkToHome={false}
              testId="agreement-terms-modal-logo"
              className="shrink-0"
            />
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Terms &amp; Conditions</div>
              <div className="mt-0.5 font-black text-base md:text-lg truncate">{reference}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="agreement-terms-close" className="p-2 rounded-full hover:bg-[#F6F5F2] shrink-0"><X size={18}/></button>
        </div>
        <div
          ref={scrollRef}
          onScroll={onScroll}
          data-testid="agreement-terms-scroll"
          className="p-5 overflow-y-auto flex-1 text-sm leading-relaxed whitespace-pre-wrap"
          style={{ minHeight: "40vh" }}
        >
          {legalText}
          <div className="mt-8 py-4 rounded-lg bg-[#FFF7EE] text-center text-[12px] font-black tracking-[0.10em] uppercase text-[#8A4A00]"
               data-testid="agreement-terms-bottom-marker">
            You've reached the end of the Terms &amp; Conditions
          </div>
        </div>
        <div className="p-4 border-t bg-white">
          <button
            type="button"
            data-testid="agreement-terms-i-have-read"
            onClick={onClose}
            disabled={!atBottom}
            className={`w-full py-3 rounded-full font-black text-[12px] tracking-[0.16em] uppercase ${
              atBottom ? "bg-[#0A0A0A] text-white hover:bg-black" : "bg-[#F6F5F2] text-[#8A8A8A] cursor-not-allowed"
            }`}
          >
            <ShieldCheck className="inline mr-1" size={13}/> I have read the Terms
          </button>
        </div>
      </div>
    </div>
  );
}
